(() => {
  const selectors = {
    root: '[data-cpc-root]',
    productJson: '[data-cpc-product-json]',
    form: '[data-cpc-product-form]',
    variantId: '[data-cpc-variant-id]',
    price: '[data-cpc-current-price]',
    comparePrice: '[data-cpc-compare-price]',
    optionButton: '[data-cpc-option-button]',
    addButton: '[data-cpc-submit]',
    stickyButton: '[data-cpc-sticky-submit]',
    stickyBar: '[data-cpc-sticky-atc]',
    stickyPrice: '[data-cpc-sticky-price]',
    stickyVariant: '[data-cpc-sticky-variant]',
    upload: '[data-cpc-upload]',
  };

  const formatMoney = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const parseJson = (root) => {
    const script = root.querySelector(selectors.productJson);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Unable to parse custom portrait product JSON', error);
      return null;
    }
  };

  const getVariantTitle = (variant) => {
    if (!variant || !Array.isArray(variant.options)) return '';
    return variant.options.filter(Boolean).join(' / ');
  };

  const findVariant = (variants, options) =>
    variants.find((variant) => variant.options?.every((option, index) => option === options[index]));

  const findFallbackVariant = (variants, options, changedIndex) =>
    variants.find((variant) => {
      if (!variant.available) return false;
      if (variant.options?.[changedIndex] !== options[changedIndex]) return false;
      return variant.options?.every((option, index) => index === changedIndex || !options[index] || option === options[index]);
    }) ||
    variants.find((variant) => variant.available && variant.options?.[changedIndex] === options[changedIndex]) ||
    variants.find((variant) => variant.options?.[changedIndex] === options[changedIndex]);

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.toggleAttribute('aria-busy', loading);
  };

  const renderCart = (cart, response, activeElement) => {
    if (!cart || !response) {
      window.location.href = window.routes?.cart_url || '/cart';
      return;
    }

    try {
      cart.setActiveElement?.(activeElement);
      cart.renderContents(response);
      cart.classList?.remove('is-empty');
      cart.querySelector?.('cart-drawer-items')?.classList.remove('is-empty');
    } catch (error) {
      console.error(error);
      cart.open?.(activeElement);
    }
  };

  const addToCartAjax = async (form) => {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData(form);

    if (cart?.getSectionsToRender) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
    }

    const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const json = await response.json();
    if (!response.ok || json.status) {
      throw new Error(json.description || json.message || 'Unable to add this item to cart.');
    }

    renderCart(cart, json, document.activeElement);
  };

  const setupGallery = (root) => {
    const slides = Array.from(root.querySelectorAll('[data-cpc-media-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-cpc-media-thumb]'));
    if (!slides.length) return { setMedia: () => {} };

    const setMedia = (mediaId) => {
      const requested = mediaId ? String(mediaId) : null;
      const activeSlide = slides.find((slide) => slide.dataset.mediaId === requested) || slides[0];
      const activeId = activeSlide.dataset.mediaId;

      slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.classList.toggle('is-active', isActive);
        slide.toggleAttribute('aria-hidden', !isActive);
        if (!isActive) {
          slide.querySelectorAll('video').forEach((video) => video.pause?.());
        }
      });

      thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', thumb.dataset.mediaId === activeId ? 'true' : 'false');
      });
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => setMedia(thumb.dataset.mediaId));
    });

    setMedia(slides.find((slide) => slide.classList.contains('is-active'))?.dataset.mediaId);
    return { setMedia };
  };

  const setupQuantity = (root) => {
    root.querySelectorAll('[data-cpc-quantity]').forEach((quantity) => {
      const input = quantity.querySelector('input[name="quantity"]');
      quantity.querySelectorAll('[data-cpc-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.cpcQuantityChange || 0);
          const current = Number(input?.value || 1);
          const next = Math.max(1, current + step);
          if (input) input.value = String(next);
        });
      });
    });
  };

  const setupPersonalization = (root) => {
    root.querySelectorAll('[data-cpc-character-input]').forEach((input) => {
      const count = root.querySelector(`[data-cpc-character-count="${input.id}"]`);
      const update = () => {
        if (count) count.textContent = `${input.value.length}/${input.maxLength || 0}`;
      };
      input.addEventListener('input', update);
      update();
    });
  };

  const setupUpload = (root) => {
    const upload = root.querySelector(selectors.upload);
    if (!upload) return null;

    const input = upload.querySelector('[data-cpc-upload-input]');
    const dropzone = upload.querySelector('[data-cpc-dropzone]');
    const meta = upload.querySelector('[data-cpc-upload-meta]');
    const fileLabel = upload.querySelector('[data-cpc-upload-file]');
    const preview = upload.querySelector('[data-cpc-upload-preview]');
    const previewImage = upload.querySelector('[data-cpc-upload-preview-image]');
    const removeButton = upload.querySelector('[data-cpc-upload-remove]');
    const progress = upload.querySelector('[data-cpc-upload-progress]');
    const progressBar = upload.querySelector('[data-cpc-upload-progress-bar]');
    const status = upload.querySelector('[data-cpc-upload-status]');
    const error = upload.querySelector('[data-cpc-upload-error]');
    const urlInput = upload.querySelector('[data-cpc-upload-url]');
    const uploadIdInput = upload.querySelector('[data-cpc-upload-id]');
    const fileNameInput = upload.querySelector('[data-cpc-upload-file-name]');
    const useCloudinary = upload.dataset.cloudinaryEnabled === 'true';

    const state = {
      file: null,
      uploadedUrl: '',
      uploadId: '',
      uploadPromise: null,
      previewUrl: '',
      useCloudinary,
    };

    const setError = (message) => {
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    };

    const setStatus = (message) => {
      if (status) status.textContent = message || '';
    };

    const setProgress = (value, visible = true) => {
      if (progress) progress.hidden = !visible;
      if (progressBar) progressBar.style.width = `${Math.max(0, Math.min(100, value))}%`;
    };

    const syncDynamicCheckout = () => {
      const dynamic = root.querySelector('.shopify-payment-button');
      if (!dynamic) return;
      const variantAvailable = root.dataset.cpcVariantAvailable !== 'false';
      const enabled = (!useCloudinary || Boolean(state.uploadedUrl)) && variantAvailable;
      dynamic.classList.toggle('cpc-dynamic-disabled', !enabled);
      dynamic.querySelectorAll('button').forEach((button) => {
        button.disabled = !enabled;
      });
    };

    const clearPreview = () => {
      if (state.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = '';
      if (previewImage) previewImage.removeAttribute('src');
      if (preview) preview.hidden = true;
    };

    const setPreview = (url) => {
      clearPreview();
      if (!url || !previewImage || !preview) return;

      state.previewUrl = url;
      previewImage.onload = () => {
        preview.hidden = false;
      };
      previewImage.onerror = () => {
        clearPreview();
      };
      previewImage.src = url;
    };

    const setPreviewFromFile = (file) => {
      if (!file) {
        clearPreview();
        return;
      }

      try {
        setPreview(URL.createObjectURL(file));
      } catch (error) {
        clearPreview();
      }
    };

    const createUploadId = () => {
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).slice(2, 10);
      return `portrait-${timestamp}-${random}`;
    };

    const reset = () => {
      state.file = null;
      state.uploadedUrl = '';
      state.uploadId = '';
      state.uploadPromise = null;
      clearPreview();
      if (input) input.value = '';
      if (urlInput) urlInput.value = '';
      if (uploadIdInput) uploadIdInput.value = '';
      if (fileNameInput) fileNameInput.value = '';
      if (meta) meta.hidden = true;
      setProgress(0, false);
      setStatus('');
      setError('');
      syncDynamicCheckout();
    };

    const uploadToCloudinary = () => {
      if (!useCloudinary || !state.file) return Promise.resolve(true);
      if (state.uploadedUrl) return Promise.resolve(true);
      if (state.uploadPromise) return state.uploadPromise;

      const cloudName = upload.dataset.cloudName;
      const uploadPreset = upload.dataset.uploadPreset;
      const folder = upload.dataset.uploadFolder;

      if (!cloudName || !uploadPreset) {
        setError('Photo upload is not configured. Please use the native upload fallback or contact the store owner.');
        return Promise.resolve(false);
      }

      state.uploadPromise = new Promise((resolve) => {
        const formData = new FormData();
        formData.append('file', state.file);
        formData.append('upload_preset', uploadPreset);
        if (folder) formData.append('folder', folder);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(cloudName)}/auto/upload`);

        xhr.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) return;
          setProgress((event.loaded / event.total) * 100);
        });

        xhr.addEventListener('load', () => {
          state.uploadPromise = null;
          if (xhr.status < 200 || xhr.status >= 300) {
            setError('We could not upload your photo. Please try again or choose another image.');
            setStatus('');
            setProgress(0, false);
            resolve(false);
            return;
          }

          try {
            const response = JSON.parse(xhr.responseText);
            state.uploadedUrl = response.secure_url || response.url || '';
            if (!state.uploadedUrl) throw new Error('Missing upload URL');
            if (urlInput) urlInput.value = state.uploadedUrl;
            if (fileNameInput) fileNameInput.value = state.file.name || '';
            setPreview(state.uploadedUrl);
            setProgress(100);
            setStatus('Photo uploaded');
            setError('');
            syncDynamicCheckout();
            resolve(true);
          } catch (error) {
            setError('We could not read the uploaded photo URL. Please try again.');
            setStatus('');
            setProgress(0, false);
            resolve(false);
          }
        });

        xhr.addEventListener('error', () => {
          state.uploadPromise = null;
          setError('We could not upload your photo. Please check your connection and try again.');
          setStatus('');
          setProgress(0, false);
          resolve(false);
        });

        setStatus('Uploading photo...');
        setProgress(5);
        xhr.send(formData);
      });

      return state.uploadPromise;
    };

    const setFile = (file) => {
      state.file = file || null;
      state.uploadedUrl = '';
      state.uploadId = file ? createUploadId() : '';
      state.uploadPromise = null;
      if (urlInput) urlInput.value = '';
      if (uploadIdInput) uploadIdInput.value = state.uploadId;
      if (fileNameInput) fileNameInput.value = file?.name || '';
      if (fileLabel) fileLabel.textContent = file?.name || '';
      if (meta) meta.hidden = !file;
      setPreviewFromFile(file);
      setError('');
      setStatus(file && useCloudinary ? 'Ready to upload' : file ? 'Photo selected' : '');
      setProgress(0, false);
      syncDynamicCheckout();

      if (file && useCloudinary) uploadToCloudinary();
    };

    input?.addEventListener('change', () => setFile(input.files?.[0]));
    removeButton?.addEventListener('click', reset);

    ['dragenter', 'dragover'].forEach((eventName) => {
      dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragging');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragging');
      });
    });

    dropzone?.addEventListener('drop', (event) => {
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
      } catch (error) {
        // Some browsers do not allow assigning FileList. Cloudinary uploads still work from state.file.
      }

      setFile(file);
    });

    root.addEventListener('cpc:variant-change', syncDynamicCheckout);
    syncDynamicCheckout();

    return {
      state,
      setError,
      uploadToCloudinary,
      validate() {
        if (state.useCloudinary) {
          if (!state.file) {
            setError(upload.dataset.requiredError || 'Please upload a photo before adding this to cart.');
            upload.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
          }
          return true;
        }

        if (!input?.files?.length) {
          setError(upload.dataset.requiredError || 'Please upload a photo before adding this to cart.');
          upload.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        setError('');
        return true;
      },
    };
  };

  const setupVariants = (root, product, gallery) => {
    const variants = product?.variants || [];
    const variantInput = root.querySelector(selectors.variantId);
    const price = root.querySelector(selectors.price);
    const comparePrice = root.querySelector(selectors.comparePrice);
    const addButton = root.querySelector(selectors.addButton);
    const stickyButton = root.querySelector(selectors.stickyButton);
    const stickyPrice = root.querySelector(selectors.stickyPrice);
    const stickyVariant = root.querySelector(selectors.stickyVariant);
    const currency = root.dataset.currency || 'USD';
    let selectedVariant = variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants.find((variant) => variant.available) || variants[0];
    let selectedOptions = selectedVariant?.options?.slice() || [];

    const updateOptionButtons = () => {
      root.querySelectorAll(selectors.optionButton).forEach((button) => {
        const index = Number(button.dataset.optionPosition || 1) - 1;
        const value = button.dataset.optionValue;
        const pressed = selectedOptions[index] === value;
        const available = variants.some((variant) => {
          if (!variant.available || variant.options?.[index] !== value) return false;
          return variant.options?.every((option, optionIndex) => optionIndex === index || !selectedOptions[optionIndex] || option === selectedOptions[optionIndex]);
        });

        button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        button.disabled = !available;
      });
    };

    const updateVariant = (variant, options = {}) => {
      selectedVariant = variant;
      if (variant?.options) selectedOptions = variant.options.slice();

      if (variantInput) {
        variantInput.value = variant?.id || '';
        variantInput.disabled = !variant || !variant.available;
      }

      root.dataset.cpcVariantAvailable = variant?.available ? 'true' : 'false';

      if (price) price.textContent = formatMoney(variant?.price || 0, currency);

      if (comparePrice) {
        const showCompare = variant?.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? formatMoney(variant.compare_at_price, currency) : '';
      }

      if (addButton) {
        addButton.disabled = !variant || !variant.available;
        addButton.querySelector('[data-cpc-submit-text]').textContent = !variant
          ? window.variantStrings?.unavailable || 'Unavailable'
          : variant.available
            ? root.dataset.addToCartLabel || window.variantStrings?.addToCart || 'Add to cart'
            : window.variantStrings?.soldOut || 'Sold out';
      }

      if (stickyButton) stickyButton.disabled = !variant || !variant.available;

      if (stickyPrice) stickyPrice.textContent = formatMoney(variant?.price || 0, currency);
      if (stickyVariant) stickyVariant.textContent = getVariantTitle(variant);

      const mediaId = variant?.featured_media?.id || variant?.featured_media_id;
      if (mediaId) gallery.setMedia(mediaId);

      updateOptionButtons();
      root.dispatchEvent(new CustomEvent('cpc:variant-change', { detail: { variant } }));

      if (options.updateUrl !== false && variant?.id && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    };

    root.querySelectorAll(selectors.optionButton).forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const index = Number(button.dataset.optionPosition || 1) - 1;
        selectedOptions[index] = button.dataset.optionValue;
        const exact = findVariant(variants, selectedOptions);
        const nextVariant = exact || findFallbackVariant(variants, selectedOptions, index);
        updateVariant(nextVariant || selectedVariant);
      });
    });

    updateVariant(selectedVariant, { updateUrl: false });
  };

  const setupForm = (root, uploadApi) => {
    const form = root.querySelector(selectors.form);
    const addButton = root.querySelector(selectors.addButton);
    const stickyButton = root.querySelector(selectors.stickyButton);
    const error = root.querySelector('[data-cpc-form-error]');
    if (!form) return;

    const setFormError = (message) => {
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    };

    const submitAddToCart = async (submitter) => {
      setFormError('');
      if (uploadApi && !uploadApi.validate()) return;

      if (uploadApi?.state.useCloudinary) {
        setButtonLoading(submitter || addButton, true);
        const uploaded = await uploadApi.uploadToCloudinary();
        if (!uploaded) {
          setButtonLoading(submitter || addButton, false);
          return;
        }

        try {
          await addToCartAjax(form);
        } catch (error) {
          console.error(error);
          setFormError(error.message || 'Unable to add this item to cart.');
        } finally {
          setButtonLoading(submitter || addButton, false);
        }
        return;
      }

      // Native Shopify file properties are not reliable through AJAX carts.
      // For fallback mode, let the browser submit the multipart product form normally.
      form.submit();
    };

    form.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      const isAddToCart = !submitter || submitter.matches(selectors.addButton) || submitter.name === 'add';

      if (!uploadApi?.validate()) {
        event.preventDefault();
        return;
      }

      if (uploadApi?.state.useCloudinary && uploadApi.state.file && !uploadApi.state.uploadedUrl) {
        event.preventDefault();
        const uploaded = await uploadApi.uploadToCloudinary();
        if (!uploaded) return;
        if (isAddToCart) {
          await submitAddToCart(submitter);
        } else {
          form.requestSubmit?.(submitter);
        }
        return;
      }

      if (isAddToCart && uploadApi?.state.useCloudinary) {
        event.preventDefault();
        await submitAddToCart(submitter);
      }
    });

    stickyButton?.addEventListener('click', () => {
      if (addButton?.disabled) return;
      submitAddToCart(stickyButton);
    });
  };

  const setupStickyAtc = (root) => {
    const stickyBar = root.querySelector(selectors.stickyBar);
    const addButton = root.querySelector(selectors.addButton);
    if (!stickyBar || !addButton) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let ticking = false;

    const setVisible = (visible) => {
      root.classList.toggle('cpc-template--sticky-visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const update = () => {
      ticking = false;

      if (!mobileQuery.matches) {
        setVisible(false);
        return;
      }

      const buttonRect = addButton.getBoundingClientRect();
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      const footerRect = footer?.getBoundingClientRect();
      const userPassedMainButton = buttonRect.bottom < 0;
      const footerIsNear = footerRect ? footerRect.top < window.innerHeight - 24 : false;

      setVisible(userPassedMainButton && !footerIsNear);
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', update);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(update);
    }

    update();
  };

  const setupFaqs = (scope = document) => {
    scope.querySelectorAll('[data-cpc-faq-button]').forEach((button) => {
      if (button.dataset.cpcInitialized === 'true') return;
      button.dataset.cpcInitialized = 'true';

      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) panel.hidden = expanded;
      });
    });
  };

  const setupScrollLinks = (scope = document) => {
    scope.querySelectorAll('[data-cpc-scroll-target]').forEach((link) => {
      if (link.dataset.cpcInitialized === 'true') return;
      link.dataset.cpcInitialized = 'true';

      link.addEventListener('click', (event) => {
        const target = document.querySelector(link.dataset.cpcScrollTarget || link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.querySelector('input, button, textarea, select')?.focus({ preventScroll: true });
      });
    });
  };

  const initRoot = (root) => {
    if (root.dataset.cpcInitialized === 'true') return;
    root.dataset.cpcInitialized = 'true';

    const product = parseJson(root);
    const gallery = setupGallery(root);
    setupQuantity(root);
    setupPersonalization(root);
    const uploadApi = setupUpload(root);
    setupVariants(root, product, gallery);
    setupForm(root, uploadApi);
    setupStickyAtc(root);
  };

  const init = (scope = document) => {
    scope.querySelectorAll(selectors.root).forEach(initRoot);
    setupFaqs(scope);
    setupScrollLinks(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
